import { Archive, ArchiveRestore, History } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ActorStringLabel } from '~/components/common/actor-string-label';
import { Button, type ButtonProps } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';
import { formatAuditTimestamp } from '~/utils/date';

import type { ArchiveHistoryEntry } from '../types/list-archive.types';

interface ArchiveHistoryPopoverProps {
  /** Newest-first history from a detail payload. Renders nothing when empty
   *  or absent — list rows never carry it (contract C7). */
  history: ArchiveHistoryEntry[] | null | undefined;
  buttonVariant?: ButtonProps['variant'];
  className?: string;
  /** E2E `data-testid` for the trigger; popover/items derive `${testId}-...`. */
  testId?: string;
}

/**
 * Icon button that opens the row's visibility history — every archive and
 * restore, who did it and when. Only real state changes appear (idempotent
 * repeats add nothing); `by` is null when no person was in scope, e.g. a re-run
 * that cleared the flag automatically, and reads as "System".
 */
export function ArchiveHistoryPopover({
  history,
  buttonVariant = 'outline',
  className,
  testId = 'archive-history-button',
}: ArchiveHistoryPopoverProps) {
  const { t } = useTranslation('common');
  if (!history || history.length === 0) return null;
  const label = t('listArchive.history.trigger');
  return (
    <Popover>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <PopoverTrigger asChild>
              <Button
                data-testid={testId}
                type="button"
                variant={buttonVariant}
                size="icon"
                aria-label={label}
                className={cn('shrink-0 cursor-pointer', className)}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <History className="h-4 w-4" aria-hidden="true" />
              </Button>
            </PopoverTrigger>
          </span>
        </TooltipTrigger>
        <TooltipContent data-testid={`${testId}-tooltip`}>{label}</TooltipContent>
      </Tooltip>
      <PopoverContent data-testid={`${testId}-popover`} align="end" className="w-80 p-0">
        <p data-testid={`${testId}-title`} className="border-b px-4 py-3 text-sm font-semibold">
          {t('listArchive.history.title')}
        </p>
        <ol data-testid={`${testId}-list`} className="max-h-72 divide-y overflow-y-auto">
          {history.map((entry, index) => {
            const archived = entry.action === 'ARCHIVED';
            const Icon = archived ? Archive : ArchiveRestore;
            return (
              <li
                key={`${entry.at}-${String(index)}`}
                data-testid={`${testId}-item-${entry.at}`}
                className="flex items-start gap-3 px-4 py-2.5"
              >
                <Icon
                  className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0"
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <span data-testid={`${testId}-item-action`} className="text-sm font-medium">
                      {archived
                        ? t('listArchive.history.archived')
                        : t('listArchive.history.unarchived')}
                    </span>
                    <span
                      data-testid={`${testId}-item-at`}
                      className="text-muted-foreground text-xs tabular-nums"
                    >
                      {formatAuditTimestamp(entry.at)}
                    </span>
                  </div>
                  <div data-testid={`${testId}-item-by`} className="text-muted-foreground text-xs">
                    {entry.by ? (
                      <ActorStringLabel value={entry.by} showAvatar={false} className="text-xs" />
                    ) : (
                      t('listArchive.history.system')
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </PopoverContent>
    </Popover>
  );
}
